import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { T, PageHeader, Section, Alert } from '../components/UI';
import { confirmDelete } from '../utils/confirmDelete';
import { StatusPill } from './AnnouncementList';
import { RichContent } from '../components/RichText';

const fmtTime = (d) => (d ? new Date(d).toLocaleString('en-AU', { day:'2-digit', month:'short', hour:'numeric', minute:'2-digit', hour12:true }) : '');
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`);

// Announcements → one announcement: what was sent, and who has received / opened it
export default function AnnouncementDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [a, setA]             = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice]   = useState(null);
  const [busy, setBusy]       = useState(false);

  const load = useCallback(() => api.get(`/announcements/${id}`)
    .then(({ data }) => { setA(data.announcement); setRecipients(data.recipients); })
    .catch(err => setNotice({ type:'error', text: err.response?.data?.message || 'Failed to load this announcement' }))
    .finally(() => setLoading(false)), [id]);

  useEffect(() => { load(); }, [load]);

  // While it's still sending, keep refreshing so progress shows up
  useEffect(() => {
    if (a?.status !== 'sending') return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [a?.status, load]);

  const retry = async () => {
    setBusy(true); setNotice(null);
    try { await api.post(`/announcements/${id}/retry`); await load(); }
    catch (err) { setNotice({ type:'error', text: err.response?.data?.message || 'Could not retry' }); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!await confirmDelete('This deletes the announcement, its uploaded files and everyone’s links. Staff who open their link afterwards will see “not available”.')) return;
    setBusy(true);
    try { await api.delete(`/announcements/${id}`); navigate('/announcements'); }
    catch (err) { setNotice({ type:'error', text: err.response?.data?.message || 'Could not delete' }); setBusy(false); }
  };

  if (loading) return <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}><i className="ti ti-loader" style={{ fontSize:'24px', color:'var(--blue)' }} aria-hidden="true"/></div>;
  if (!a) return <div style={{ padding:'40px', color:'var(--red)', textAlign:'center' }}>{notice?.text || 'Announcement not found'}</div>;

  const sent   = recipients.filter(r => r.sendStatus === 'sent').length;
  const failed = recipients.filter(r => r.sendStatus === 'failed').length;
  const opened = recipients.filter(r => r.openedAt).length;
  const stuck  = a.status === 'sending' && Date.now() - new Date(a.updatedAt).getTime() > 5 * 60 * 1000;

  const chip = (label, value, color) => (
    <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'12px 14px', flex:'1 1 110px' }}>
      <div style={{ fontSize:'11px', color:'var(--muted)', marginBottom:'4px' }}>{label}</div>
      <div style={{ fontSize:'20px', fontWeight:'500', color }}>{value}</div>
    </div>
  );
  const th = { padding:'9px 12px', textAlign:'left', fontSize:'11px', fontWeight:'500', textTransform:'uppercase', letterSpacing:'.5px', color:'var(--muted)', borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' };
  const td = { padding:'10px 12px', fontSize:'13px', verticalAlign:'middle' };

  return (
    <div style={T.page}>
      <PageHeader title={a.title} onBack={()=>navigate('/announcements')} actions={
        <button onClick={remove} disabled={busy} title="Delete" style={{ ...T.btnIcon, color:'var(--red)', borderColor:'rgba(163,45,45,0.25)' }}><i className="ti ti-trash" aria-label="Delete"/></button>
      }/>
      <div style={{ maxWidth:'800px', margin:'0 auto', padding:'20px 16px 60px' }}>
        {notice && <Alert type={notice.type}>{notice.text}</Alert>}

        <div style={{ display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap', marginBottom:'14px' }}>
          <StatusPill status={a.status} failed={failed > 0}/>
          <span style={{ fontSize:'12px', color:'var(--muted)' }}>{a.status === 'draft' ? `Created ${fmtTime(a.createdAt)} — not sent` : `Sent ${fmtTime(a.sentAt || a.createdAt)}`} · by {a.createdByName}</span>
          {(failed > 0 || stuck) && a.status !== 'draft' && (
            <button onClick={retry} disabled={busy} style={{ ...T.btnPrimary, marginLeft:'auto', opacity:busy?0.6:1 }}><i className="ti ti-refresh" aria-hidden="true"/> {stuck ? 'Resume sending' : `Retry ${failed} failed`}</button>
          )}
        </div>

        {a.status !== 'draft' && (
          <div style={{ display:'flex', gap:'10px', flexWrap:'wrap', marginBottom:'16px' }}>
            {chip('Sent', `${sent} / ${recipients.length}`, 'var(--blue)')}
            {chip('Opened', `${opened} / ${recipients.length}`, 'var(--green)')}
            {chip('Failed', failed, failed ? 'var(--red)' : 'var(--muted)')}
          </div>
        )}

        <Section title="Message" accent>
          {a.messageHtml
            ? <RichContent html={a.messageHtml}/>
            : <div style={{ whiteSpace:'pre-wrap', fontSize:'14px', color:'var(--text)', lineHeight:1.6 }}>{a.message}</div>}
        </Section>

        {(a.videos.length > 0 || a.files.length > 0) && (
          <Section title="Videos and files" accent>
            {a.videos.map(v => (
              <div key={v.videoId} style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'13px', marginBottom:'6px' }}>
                <i className="ti ti-brand-youtube" style={{ color:'var(--red)', fontSize:'18px' }} aria-hidden="true"/>
                <a href={`https://youtu.be/${v.videoId}`} target="_blank" rel="noreferrer" style={{ color:'var(--blue)' }}>youtu.be/{v.videoId}</a>
              </div>
            ))}
            {a.files.map(f => (
              <div key={f.fileId} style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'13px', marginBottom:'6px' }}>
                <i className="ti ti-file" style={{ color:'var(--blue)', fontSize:'18px' }} aria-hidden="true"/>{f.name} <span style={{ color:'var(--muted)' }}>· {fmtSize(f.size)}</span>
              </div>
            ))}
          </Section>
        )}

        <Section title={`Recipients (${recipients.length})`} accent>
          <div className="table-scroll">
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>{['Name','Email','Email status','Opened'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {recipients.map(r => (
                  <tr key={r._id} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={{ ...td, fontWeight:'500' }}>{r.name}</td>
                    <td style={td}>{r.email}</td>
                    <td style={td}>
                      {r.sendStatus === 'sent' && <span style={{ color:'var(--green)' }}>Sent</span>}
                      {r.sendStatus === 'queued' && <span style={{ color:'var(--amber)' }}>{a.status === 'draft' ? 'Not sent yet' : 'Sending…'}</span>}
                      {r.sendStatus === 'failed' && <span style={{ color:'var(--red)' }} title={r.sendError}>Failed</span>}
                    </td>
                    <td style={td}>{r.openedAt ? <span style={{ color:'var(--green)' }}><i className="ti ti-check" aria-hidden="true"/> {fmtTime(r.openedAt)}{r.openCount > 1 ? ` · ${r.openCount}×` : ''}</span> : <span style={{ color:'var(--hint)' }}>—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    </div>
  );
}
